import { useEffect, useState } from 'react';
import { Building2, Save, CheckCircle2, AlertTriangle, Info, Plus, X, Circle } from 'lucide-react';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import Tooltip from '@/components/ui/Tooltip';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

/**
 * Business Profile — the canonical "who are we" record that supplier
 * dealer/reseller applications ask for. Dippy's draft_dealer_application
 * tool reads this data at negotiation time; without it, Dippy can't
 * fill out forms and has to escalate every dealer application to the
 * admin empty-handed.
 */

type BusinessStage = 'pre_launch' | 'startup' | 'growing' | 'established';

interface BusinessProfile {
  business_stage?: BusinessStage;
  legal_name?: string;
  dba?: string;
  business_type?: 'sole_proprietor' | 'llc' | 'c_corp' | 's_corp' | 'partnership' | 'other';
  ein?: string;
  years_in_business?: number;
  website?: string;
  primary_email?: string;
  primary_phone?: string;
  billing_address?: string;
  shipping_address?: string;
  owner_name?: string;
  owner_title?: string;
  owner_email?: string;
  owner_phone?: string;
  resale_cert_number?: string;
  resale_cert_state?: string;
  tax_exempt?: boolean;
  annual_revenue_estimate?: string;
  projected_annual_purchases?: string;
  projected_monthly_orders?: number;
  product_categories?: string[];
  service_area?: string;
  has_physical_retail?: boolean;
  has_ecommerce?: boolean;
  bank_reference?: string;
  trade_references?: string[];
  eccn_declaration?: string;
  export_license_number?: string;
  exports_to?: string[];
}

interface ProfileResponse {
  profile: BusinessProfile;
  updated_at: string | null;
  populated_fields: string[];
  critical_missing_fields: string[];
  is_complete: boolean;
  critical_fields: string[];
}

const BUSINESS_TYPE_OPTIONS = [
  { value: '', label: '— Select —' },
  { value: 'sole_proprietor', label: 'Sole proprietor' },
  { value: 'llc', label: 'LLC' },
  { value: 'c_corp', label: 'C corporation' },
  { value: 's_corp', label: 'S corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
];

const REVENUE_BANDS = [
  '',
  'Under $100k',
  '$100k–$250k',
  '$250k–$500k',
  '$500k–$1M',
  '$1M–$5M',
  '$5M+',
];

const BUSINESS_STAGE_OPTIONS: Array<{
  value: BusinessStage;
  label: string;
  description: string;
}> = [
  {
    value: 'pre_launch',
    label: 'Pre-launch',
    description: "No sales yet. Dippy will introduce you as 'launching soon, preparing supplier roster' and won't claim existing customers or trade references.",
  },
  {
    value: 'startup',
    label: 'Startup (0–1 year)',
    description: "Early revenue, still building. Dippy will frame any volume numbers as projections (e.g., 'projecting $50k in year-one orders') rather than claimed history.",
  },
  {
    value: 'growing',
    label: 'Growing (1–3 years)',
    description: "Established revenue, gathering momentum. Dippy will cite actual annual revenue and any trade references you've added.",
  },
  {
    value: 'established',
    label: 'Established (3+ years)',
    description: "Proven track record. Dippy will use full claimed history and volume commitments to negotiate terms aggressively.",
  },
];

const RELAXED_STAGES: BusinessStage[] = ['pre_launch', 'startup'];
const isRelaxedStage = (s: BusinessStage | undefined): boolean =>
  !!s && RELAXED_STAGES.includes(s);

// Short labels + "why this matters" hints for every field that shows up in the
// critical-missing checklist at the top of the page. Keep hints to one
// sentence — this is the cheat-sheet view, not the full helper copy.
const CRITICAL_FIELD_META: Record<string, { label: string; hint: string }> = {
  business_stage: {
    label: 'Business stage',
    hint: "Pre-launch / startup / growing / established. Dippy uses this to decide whether to claim history or frame everything as projections.",
  },
  legal_name: {
    label: 'Legal business name',
    hint: 'Registered entity name exactly as it appears on tax filings. Goes at the top of every dealer application.',
  },
  business_type: {
    label: 'Business type',
    hint: 'LLC, S-corp, sole proprietor, etc. Determines which contract template a supplier routes you to.',
  },
  ein: {
    label: 'Federal EIN',
    hint: '9-digit IRS Employer Identification Number. Required by every US supplier for tax reporting. Free at irs.gov/ein.',
  },
  billing_address: {
    label: 'Billing address',
    hint: 'Full street + city + state + ZIP where supplier invoices get mailed.',
  },
  owner_name: {
    label: 'Owner / principal name',
    hint: 'The human who signs contracts — owner, Managing Member, or officer (not a staffer).',
  },
  owner_email: {
    label: 'Owner email',
    hint: 'Direct line for the signer so legal docs reach a decision-maker, not the general inbox.',
  },
  resale_cert_number: {
    label: 'Resale certificate number',
    hint: 'Your state sales-tax permit so suppliers ship tax-free. Free at your state Department of Revenue.',
  },
  annual_revenue_estimate: {
    label: 'Annual revenue estimate',
    hint: 'Rough band. Suppliers tier pricing and credit terms from this. N/A if pre-launch or startup.',
  },
};

function scrollToField(fieldKey: string) {
  const el = document.getElementById(`field-${fieldKey}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Flash a ring so the user's eye lands on the field they clicked.
  el.classList.add('ring-2', 'ring-offset-2', 'ring-green-400', 'rounded-md');
  window.setTimeout(() => {
    el.classList.remove('ring-2', 'ring-offset-2', 'ring-green-400', 'rounded-md');
  }, 1800);
}

function Field({
  label,
  required,
  helperText,
  children,
  id,
}: {
  label: string;
  required?: boolean;
  helperText: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <div className="space-y-1" id={id ? `field-${id}` : undefined}>
      <label className="block text-sm font-medium text-stone-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      <p className="text-xs text-stone-500 leading-relaxed flex items-start gap-1.5">
        <Info className="w-3 h-3 shrink-0 mt-0.5 text-stone-400" />
        <span>{helperText}</span>
      </p>
    </div>
  );
}

function ChipInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState('');
  function add() {
    const s = draft.trim();
    if (!s || value.includes(s)) return;
    onChange([...value, s]);
    setDraft('');
  }
  return (
    <div>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 bg-stone-600 text-white rounded-lg text-sm hover:bg-stone-700"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 text-stone-700 text-sm rounded-full"
            >
              {v}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== v))}
                className="hover:bg-stone-200 rounded-full p-0.5"
                aria-label={`Remove ${v}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BusinessProfilePage() {
  const [profile, setProfile] = useState<BusinessProfile>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [completeness, setCompleteness] = useState<{
    populated_fields: string[];
    critical_missing_fields: string[];
    is_complete: boolean;
  }>({ populated_fields: [], critical_missing_fields: [], is_complete: false });
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ProfileResponse>('/admin/business-profile')
      .then((res) => {
        if (cancelled) return;
        setProfile(res.profile || {});
        setCompleteness({
          populated_fields: res.populated_fields,
          critical_missing_fields: res.critical_missing_fields,
          is_complete: res.is_complete,
        });
      })
      .catch((err) => addToast(`Failed to load business profile: ${err.message}`, 'error'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update<K extends keyof BusinessProfile>(key: K, value: BusinessProfile[K]) {
    setProfile((p) => ({ ...p, [key]: value }));
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const res = await api.put<ProfileResponse>('/admin/business-profile', profile);
      setProfile(res.profile || {});
      setCompleteness({
        populated_fields: res.populated_fields,
        critical_missing_fields: res.critical_missing_fields,
        is_complete: res.is_complete,
      });
      addToast('Business profile saved', 'success');
    } catch (err: any) {
      addToast(`Save failed: ${err?.message || 'unknown error'}`, 'error');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <div className="p-8 text-center text-stone-500">Loading business profile…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6 pb-24">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-stone-900 flex items-center gap-2">
          <Building2 className="w-6 h-6" />
          Business Profile
        </h1>
        <p className="text-sm text-stone-600 mt-1">
          Suppliers ask for this information every time you apply to a dealer/wholesale program.
          Fill it out once here and Dippy will reuse it to draft every dealer application automatically —
          DJI, Autel, Drone Nerds, Horizon Hobby, and any other authorized-distributor onboarding form.
        </p>
      </div>

      {/* Critical-fields cheat sheet — glanceable checklist of what Dippy
          needs to auto-fill dealer applications. Each unchecked item jumps
          to its field on click and exposes a tooltip with the "why". */}
      {(() => {
        const relaxed = isRelaxedStage(profile.business_stage);
        const orderedKeys = Object.keys(CRITICAL_FIELD_META).filter(
          (k) => !(relaxed && k === 'annual_revenue_estimate')
        );
        const populatedSet = new Set(completeness.populated_fields);
        const total = orderedKeys.length;
        const done = orderedKeys.filter((k) => populatedSet.has(k)).length;
        return (
          <Card
            padding="md"
            className={cn(
              'border-2',
              completeness.is_complete
                ? 'border-green-300 bg-green-50'
                : 'border-amber-300 bg-amber-50'
            )}
          >
            <div className="flex items-start gap-3">
              {completeness.is_complete ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                  <div className="font-semibold text-stone-900">
                    {completeness.is_complete
                      ? 'Profile complete — Dippy can auto-fill dealer applications'
                      : 'What Dippy needs from you'}
                  </div>
                  {!completeness.is_complete && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">
                      {done} of {total} complete
                    </span>
                  )}
                </div>
                <div className="text-sm text-stone-700 mt-0.5 mb-3">
                  {completeness.is_complete
                    ? "Dippy's draft_dealer_application tool will pull from this record when a supplier asks for paperwork."
                    : 'Click any item to jump to that field. Hover the help icon for why it matters.'}
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                  {orderedKeys.map((key) => {
                    const populated = populatedSet.has(key);
                    const meta = CRITICAL_FIELD_META[key];
                    return (
                      <li key={key} className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => scrollToField(key)}
                          className={cn(
                            'flex items-center gap-2 text-sm flex-1 min-w-0 text-left py-1 px-1 -mx-1 rounded hover:bg-white/60 focus:outline-none focus:ring-2 focus:ring-green-500 transition',
                            populated ? 'text-stone-500' : 'text-stone-800'
                          )}
                          aria-label={
                            populated
                              ? `${meta.label} — complete, jump to field`
                              : `${meta.label} — missing, jump to field`
                          }
                        >
                          {populated ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                          <span
                            className={cn(
                              'truncate',
                              populated ? 'line-through' : 'font-medium'
                            )}
                          >
                            {meta.label}
                          </span>
                        </button>
                        {!populated && <Tooltip content={meta.hint} />}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Section 0 — Business Stage */}
      <div id="field-business_stage">
      <Card title="Business Stage" padding="lg">
        <p className="text-sm text-stone-600 mb-4">
          Pick the stage that describes you today. Dippy uses this to represent your business honestly to suppliers —
          pre-launch businesses get "launching soon" language, startups get projection language, established stores
          get full claimed history. <span className="font-semibold">Don't guess high.</span> Sophisticated suppliers
          see through inflated claims and it burns the relationship.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {BUSINESS_STAGE_OPTIONS.map((opt) => {
            const selected = profile.business_stage === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update('business_stage', opt.value)}
                className={cn(
                  'text-left p-4 rounded-lg border-2 transition',
                  selected
                    ? 'border-green-500 bg-green-50'
                    : 'border-stone-200 hover:border-stone-300 bg-white'
                )}
              >
                <div className="flex items-start gap-2">
                  <div
                    className={cn(
                      'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0',
                      selected ? 'border-green-500 bg-green-500' : 'border-stone-300'
                    )}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-stone-900 text-sm">{opt.label}</div>
                    <div className="text-xs text-stone-600 mt-1 leading-relaxed">{opt.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>
      </div>

      {/* Section 1 — Identity */}
      <Card title="Business Identity" padding="lg">
        <div className="space-y-4">
          <Field
            id="legal_name"
            label="Legal business name"
            required
            helperText="Your registered legal entity name exactly as it appears on tax filings and bank accounts. Example: 'Lakeshore Avionics LLC'. This goes at the top of every dealer application."
          >
            <Input
              value={profile.legal_name || ''}
              onChange={(e) => update('legal_name', e.target.value)}
              placeholder="Your Legal Entity Name, LLC"
            />
          </Field>

          <Field
            label="Doing Business As (DBA)"
            helperText="Your public brand/store name if different from the legal name. Leave blank if you sell under the legal name. Example: 'Lakeshore Avionics' (DBA of 'Lakeshore Holdings LLC')."
          >
            <Input
              value={profile.dba || ''}
              onChange={(e) => update('dba', e.target.value)}
              placeholder="Your public brand name"
            />
          </Field>

          <Field
            id="business_type"
            label="Business type"
            required
            helperText="Your legal entity structure. Most dealer applications ask this to route you to the right contract template (LLC and corps get Master Service Agreements; sole props get a shorter form)."
          >
            <select
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              value={profile.business_type || ''}
              onChange={(e) => update('business_type', (e.target.value || undefined) as BusinessProfile['business_type'])}
            >
              {BUSINESS_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            id="ein"
            label="Federal EIN"
            required
            helperText="Your 9-digit IRS Employer Identification Number (format NN-NNNNNNN). Required by every US supplier for tax reporting and credit checks. Sole proprietors without an EIN can use their SSN, but we strongly recommend getting a free EIN at irs.gov/ein."
          >
            <Input
              value={profile.ein || ''}
              onChange={(e) => update('ein', e.target.value)}
              placeholder="12-3456789"
            />
          </Field>

          <Field
            label="Years in business"
            helperText={
              profile.business_stage === 'pre_launch'
                ? "Put 0. Dippy will position you as pre-launch and won't claim any operating history."
                : "Full years since your business was registered. Some suppliers have minimum age requirements (e.g., DJI Enterprise requires 2+ years). A lower number isn't disqualifying — they'll ask for bank/trade references instead."
            }
          >
            <Input
              type="number"
              min={0}
              value={profile.years_in_business ?? ''}
              onChange={(e) => update('years_in_business', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="3"
            />
          </Field>

          <Field
            label="Business website"
            helperText="Your public storefront URL. Suppliers visit this to verify you're a real operating retailer before approving dealer status. If you have multiple sites, list the primary one."
          >
            <Input
              value={profile.website || ''}
              onChange={(e) => update('website', e.target.value)}
              placeholder="https://yourdomain.com"
            />
          </Field>
        </div>
      </Card>

      {/* Section 2 — Contact & Address */}
      <Card title="Contact & Addresses" padding="lg">
        <div className="space-y-4">
          <Field
            label="Primary business email"
            helperText="The general business inbox (e.g., sales@ or info@). Suppliers send order confirmations, price lists, and announcements here. Make sure it's monitored — not a dead alias."
          >
            <Input
              type="email"
              value={profile.primary_email || ''}
              onChange={(e) => update('primary_email', e.target.value)}
              placeholder="sales@yourdomain.com"
            />
          </Field>

          <Field
            label="Primary business phone"
            helperText="A number a supplier rep can actually call to reach a human. Mobile is fine if you're small; make sure voicemail identifies the business."
          >
            <Input
              value={profile.primary_phone || ''}
              onChange={(e) => update('primary_phone', e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
          </Field>

          <Field
            id="billing_address"
            label="Billing address"
            required
            helperText="Full street + city + state + ZIP where invoices should be mailed. This is the address on your state business registration. Multi-line is fine — include the suite/unit."
          >
            <Textarea
              rows={3}
              value={profile.billing_address || ''}
              onChange={(e) => update('billing_address', e.target.value)}
              placeholder={'Your Company Name\n123 Main St, Suite 200\nCity, State ZIP'}
            />
          </Field>

          <Field
            label="Shipping / receiving address"
            helperText="Where sample units and dropship inventory get delivered. Leave blank to use the billing address. If you ship from a 3PL warehouse, put that address here."
          >
            <Textarea
              rows={3}
              value={profile.shipping_address || ''}
              onChange={(e) => update('shipping_address', e.target.value)}
              placeholder="Same as billing (leave blank) OR warehouse address"
            />
          </Field>
        </div>
      </Card>

      {/* Section 3 — Owner */}
      <Card title="Owner / Principal Officer" padding="lg">
        <div className="space-y-4">
          <Field
            id="owner_name"
            label="Owner / principal name"
            required
            helperText="The human who signs contracts on behalf of the business. Most dealer applications require the owner or an officer (CEO, President, Managing Member) to sign — not a staffer."
          >
            <Input
              value={profile.owner_name || ''}
              onChange={(e) => update('owner_name', e.target.value)}
              placeholder="Jane Smith"
            />
          </Field>

          <Field
            label="Owner title"
            helperText="Legal title on file with the state (Owner, Managing Member, President, CEO). Must match the business structure — an LLC has Managing Members, a corp has officers."
          >
            <Input
              value={profile.owner_title || ''}
              onChange={(e) => update('owner_title', e.target.value)}
              placeholder="Managing Member"
            />
          </Field>

          <Field
            id="owner_email"
            label="Owner email"
            required
            helperText="Direct email for the owner. Separate from the general business inbox so time-sensitive supplier docs (signed contracts, NDAs, audit requests) reach a decision-maker."
          >
            <Input
              type="email"
              value={profile.owner_email || ''}
              onChange={(e) => update('owner_email', e.target.value)}
              placeholder="owner@yourdomain.com"
            />
          </Field>

          <Field
            label="Owner phone"
            helperText="Owner's direct line for escalations. Used when a supplier's credit or legal team needs to verify the signer."
          >
            <Input
              value={profile.owner_phone || ''}
              onChange={(e) => update('owner_phone', e.target.value)}
              placeholder="+1 (555) 987-6543"
            />
          </Field>
        </div>
      </Card>

      {/* Section 4 — Tax */}
      <Card title="Tax & Resale Certificates" padding="lg">
        <div className="space-y-4">
          <Field
            id="resale_cert_number"
            label="Resale certificate number"
            required
            helperText="Your state-issued resale certificate / sales tax permit number. Required by every US wholesale supplier so they can ship to you tax-free (you collect tax from the end customer, not them). Apply at your state's Department of Revenue — it's free in most states."
          >
            <Input
              value={profile.resale_cert_number || ''}
              onChange={(e) => update('resale_cert_number', e.target.value)}
              placeholder="FL-RC-99887766"
            />
          </Field>

          <Field
            label="Resale certificate state"
            helperText="The US state that issued your resale certificate. A supplier needs this to validate the number against the correct state's tax registry."
          >
            <Input
              value={profile.resale_cert_state || ''}
              onChange={(e) => update('resale_cert_state', e.target.value)}
              placeholder="FL"
            />
          </Field>

          <Field
            label="Tax exempt"
            helperText="Check only if your business is a 501(c)(3) non-profit or otherwise sales-tax-exempt. For normal for-profit retailers this stays unchecked — the resale certificate already exempts you on inventory purchases."
          >
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!profile.tax_exempt}
                onChange={(e) => update('tax_exempt', e.target.checked)}
                className="h-4 w-4 rounded border-stone-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-stone-700">Yes, this business is tax-exempt</span>
            </label>
          </Field>
        </div>
      </Card>

      {/* Section 5 — Operations */}
      <Card title="Operations & Volume" padding="lg">
        <div className="space-y-4">
          <Field
            id="annual_revenue_estimate"
            label="Annual revenue estimate"
            required
            helperText="Your store's total annual revenue — an honest rough estimate is fine. Suppliers use this to tier you into pricing brackets. Too-high inflates your MOQ obligations; too-low gets you auto-declined. For a new store, use projected revenue for Year 1."
          >
            <select
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              value={profile.annual_revenue_estimate || ''}
              onChange={(e) => update('annual_revenue_estimate', e.target.value || undefined)}
            >
              {REVENUE_BANDS.map((b) => (
                <option key={b} value={b}>
                  {b || '— Select —'}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Projected annual purchases from this category"
            helperText="How much you expect to buy per year from suppliers in this product category. Keep realistic — underpromising is better than overpromising. For a new store pivot to 'Year 1 projection: $X'."
          >
            <Input
              value={profile.projected_annual_purchases || ''}
              onChange={(e) => update('projected_annual_purchases', e.target.value)}
              placeholder="$50k–$100k Year 1"
            />
          </Field>

          <Field
            label="Projected monthly orders"
            helperText={
              isRelaxedStage(profile.business_stage)
                ? "Expected monthly PO volume. Dippy will label these as projections in supplier emails — OK to leave blank if too early to estimate."
                : "Expected monthly order volume (number of purchase orders, not units). Suppliers use this to decide whether you qualify for credit terms or must prepay."
            }
          >
            <Input
              type="number"
              min={0}
              value={profile.projected_monthly_orders ?? ''}
              onChange={(e) => update('projected_monthly_orders', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="10"
            />
          </Field>

          <Field
            label="Product categories"
            helperText="Categories you carry. Press Enter to add each. Suppliers ask this to confirm you're a fit for their product lines. Be specific: 'consumer FPV drones' is better than 'drones'."
          >
            <ChipInput
              value={profile.product_categories || []}
              onChange={(next) => update('product_categories', next)}
              placeholder="consumer drones"
            />
          </Field>

          <Field
            label="Service area"
            helperText="Where you sell/ship to. Most US retailers are 'US only' or 'US + Canada'. International changes the dealer application because you'll need an export license."
          >
            <Input
              value={profile.service_area || ''}
              onChange={(e) => update('service_area', e.target.value)}
              placeholder="US + Canada"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Physical retail location?"
              helperText="Do you operate a brick-and-mortar store? Some premium brands (e.g., DJI Enterprise) require a physical location. E-commerce-only is fine for most."
            >
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!profile.has_physical_retail}
                  onChange={(e) => update('has_physical_retail', e.target.checked)}
                  className="h-4 w-4 rounded border-stone-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-stone-700">Yes — brick-and-mortar store</span>
              </label>
            </Field>

            <Field
              label="E-commerce store?"
              helperText="Do you sell online? This is almost always yes for a Rumio-hosted store."
            >
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={profile.has_ecommerce ?? true}
                  onChange={(e) => update('has_ecommerce', e.target.checked)}
                  className="h-4 w-4 rounded border-stone-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-stone-700">Yes — online storefront</span>
              </label>
            </Field>
          </div>
        </div>
      </Card>

      {/* Section 6 — References */}
      <Card title="Financial References" padding="lg">
        <div className="space-y-4">
          <Field
            label="Bank reference"
            helperText="Your business bank's name + a contact person or branch phone. Some suppliers (especially larger distributors) do a banker's-check before extending Net-30 terms. Leave blank if you're paying credit-card-only."
          >
            <Textarea
              rows={2}
              value={profile.bank_reference || ''}
              onChange={(e) => update('bank_reference', e.target.value)}
              placeholder={'Chase Business Banking\n123 Main St, Miami, FL 33101\nBranch phone: (305) 555-0100'}
            />
          </Field>

          <Field
            label="Trade references"
            helperText={
              isRelaxedStage(profile.business_stage)
                ? "Other suppliers you have a relationship with. If you don't have any yet, leave blank — Dippy will tell suppliers 'new business, no trade references yet' rather than fabricate."
                : "Other suppliers you already have a relationship with (3 is the standard ask). Each entry: 'Supplier Name — contact email — years of relationship'. Press Enter to add."
            }
          >
            <ChipInput
              value={profile.trade_references || []}
              onChange={(next) => update('trade_references', next)}
              placeholder="HobbyKing — accounts@hobbyking.com — 2 years"
            />
          </Field>
        </div>
      </Card>

      {/* Section 7 — Export controls */}
      <Card title="Export Controls" padding="lg">
        <div className="space-y-4">
          <Field
            label="ECCN declaration"
            helperText="For drone, electronics, and dual-use-tech stores. A plain-English statement about how you handle Export Control Classification Numbers on regulated products. Example: 'Products sold by [Your DBA] fall under ECCN 9A991 and EAR99 general commercial use. Orders to restricted countries are auto-blocked at checkout.' If you don't export anything, say 'Domestic sales only — no ECCN handling required.'"
          >
            <Textarea
              rows={4}
              value={profile.eccn_declaration || ''}
              onChange={(e) => update('eccn_declaration', e.target.value)}
              placeholder="Domestic US sales only — no ECCN / export handling required."
            />
          </Field>

          <Field
            label="Export license number (if any)"
            helperText="BIS-issued export license number, if you have one. Most retailers don't — leave blank if you only ship within the US."
          >
            <Input
              value={profile.export_license_number || ''}
              onChange={(e) => update('export_license_number', e.target.value)}
              placeholder="(leave blank if domestic only)"
            />
          </Field>

          <Field
            label="Countries exported to"
            helperText="List each country you ship to internationally. Press Enter after each. Leave empty if you're US-only — this auto-answers 'no' on the 'will you export?' question on dealer applications."
          >
            <ChipInput
              value={profile.exports_to || []}
              onChange={(next) => update('exports_to', next)}
              placeholder="Canada"
            />
          </Field>
        </div>
      </Card>

      {/* Desktop save bar */}
      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-stone-500">
          {completeness.populated_fields.length} fields populated · Dippy reads this record at task time.
        </div>
        <Button variant="primary" onClick={handleSave} isLoading={isSaving} disabled={isSaving}>
          <Save className="w-4 h-4" />
          Save Business Profile
        </Button>
      </div>
    </div>
  );
}
